
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ReviewForm } from "@/components/reviews/ReviewForm";
import { ReviewList } from "@/components/reviews/ReviewList";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkle, Star, RefreshCw } from "lucide-react";
import { useReviewsQuery, Review } from "@/hooks/useReviewsQuery";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLocation } from "react-router-dom";

interface ReviewSectionProps {
  serviceId: number;
}

export const ReviewSection = ({ serviceId }: ReviewSectionProps) => {
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { toast } = useToast();
  const [refreshing, setRefreshing] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();
  
  const { 
    highlightedReviews, 
    regularReviews, 
    isLoading,
    error,
    refetch
  } = useReviewsQuery(serviceId);

  // Force refetch when location changes (this catches navigating back to the page)
  useEffect(() => {
    console.log('Location changed, refetching reviews');
    refetch();
  }, [location.pathname, refetch]);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };
    checkAuth();
  }, []);

  useEffect(() => {
    // Reset refreshing state once loading is complete
    if (!isLoading && refreshing) {
      setRefreshing(false);
    }
  }, [isLoading, refreshing]);

  const handleReviewSubmitted = () => {
    setShowReviewForm(false);
    console.log('Review submitted, refreshing data');
    refetch();
  };

  const handleRefresh = () => {
    setRefreshing(true);
    refetch();
  };

  // Function to render stars for highlighted reviews with brand colors
  const renderStars = (rating: number) => {
    return Array(5)
      .fill(0)
      .map((_, i) => (
        <Star
          key={i}
          className={`w-3 h-3 md:w-4 md:h-4 ${
            i < rating
              ? "fill-current"
              : "text-gray-300"
          }`}
          style={{ color: i < rating ? 'var(--brand-color-3, #91DA73)' : undefined }}
        />
      ));
  };

  // Log counts for debugging
  console.log('ReviewSection - Reviews count:', {
    highlighted: highlightedReviews?.length || 0,
    regular: regularReviews?.length || 0,
    loading: isLoading,
    error: !!error
  });

  // Add CSS for calendar hover effect
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .fc-daygrid-day:hover,
      .fc-timegrid-slot:hover,
      .fc-col-header-cell:hover {
        background-color: var(--brand-color-5, #F5FBEF) !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="mt-6 md:mt-8">
      <div>
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h2 className="text-lg font-semibold">Reviews</h2>
          <div className="flex gap-2">
            <Button variant="outline" 
              onClick={handleRefresh}
              disabled={isLoading || refreshing}
              className="h-8 text-xs md:text-sm"
              size={isMobile ? "sm" : "default"}
            >
              <RefreshCw className={`h-3 w-3 md:h-4 md:w-4 mr-1 ${refreshing || isLoading ? 'animate-spin' : ''}`} />
              {isMobile ? "" : "Refresh"}
            </Button>
            <Button 
              onClick={() => setShowReviewForm(true)}
              className="h-8 text-xs md:text-sm"
              size={isMobile ? "sm" : "default"}
            >
              {isMobile ? "Review" : "Write a Review"}
            </Button>
          </div>
        </div>

        {showReviewForm && (
          <div className="mb-6">
            <ReviewForm
              serviceId={serviceId}
              onReviewSubmitted={handleReviewSubmitted}
              isAuthenticated={isAuthenticated}
            />
          </div>
        )}

        {/* Loading state - Ensure this renders consistently */}
        {isLoading && (
          <div className="text-center py-4">
            <RefreshCw className="h-5 w-5 md:h-6 md:w-6 animate-spin mx-auto text-primary mb-2" />
            <p className="text-sm text-gray-500">Loading reviews...</p>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="text-center py-4 text-red-500">
            <p>Failed to load reviews. Please try refreshing.</p>
          </div>
        )}

        {/* Content area - Only render when we have data and not loading */}
        {!isLoading && !error && (
          <>
            {/* Highlighted Reviews Section - With proper key management and brand colors */}
            {highlightedReviews && highlightedReviews.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-1 md:gap-2 mb-3 md:mb-4">
                  <Sparkle className="h-4 w-4 md:h-5 md:w-5 text-yellow-400" />
                  <h3 className="text-base md:text-lg font-medium">Highlighted Reviews</h3>
                </div>
                <div className="grid gap-3 md:gap-4 grid-cols-1 md:grid-cols-2">
                  {highlightedReviews.map((review: Review) => (
                    <Card 
                      key={`highlighted-${review.id}`} 
                      className="overflow-hidden transition-colors duration-300 border-0"
                      style={{ backgroundColor: 'var(--brand-color-5, #F5FBEF)' }}
                    >
                      <CardContent className="p-3 md:p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium truncate max-w-[60%] text-gray-800">
                            {review.customer_name || "Anonymous"}
                          </span>
                          <div className="flex items-center">
                            {renderStars(review.rating)}
                          </div>
                        </div>
                        {review.comment && (
                          <p className="text-xs md:text-sm text-gray-700 mt-1 md:mt-2 italic line-clamp-3">{review.comment}</p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 md:mt-6">
              {regularReviews && regularReviews.length > 0 ? (
                <ReviewList reviews={regularReviews} />
              ) : (
                (!highlightedReviews || highlightedReviews.length === 0) && (
                  <p className="text-sm text-gray-500 text-center py-4">No reviews yet. Be the first to review!</p>
                )
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
